import React, { useState, useEffect, useCallback } from 'react';
import { agentsApi } from '../api/agentsApi';
import { skillsApi } from '../api/skillsApi';
import type { Agent, AgentCreate, AgentUpdate, ModelTier, Skill } from '../types/contracts';
import Spinner     from '../components/Spinner';
import ErrorBanner from '../components/ErrorBanner';
import Toggle      from '../components/Toggle';
import { haptic, showMainButton, hideMainButton } from '../telegram';

const TIERS: ModelTier[] = ['brain', 'mid', 'cheap'];
const TIER_LABEL: Record<ModelTier, string> = { brain: '🧠 Brain', mid: '⚡ Mid', cheap: '💰 Cheap' };
const EMPTY: AgentCreate = { name: '', system_prompt: '', tier: 'mid', skills: [], enabled: true };

// ---- shared inline styles ----
const headerSt: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px',
  background: 'var(--tg-theme-header-bg-color)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};
const h1St: React.CSSProperties = { margin: 0, fontSize: 18, fontWeight: 600 };
const pageSt: React.CSSProperties = { minHeight: '100vh', display: 'flex', flexDirection: 'column' };
const scrollSt: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '16px 16px 80px' };
const cardSt: React.CSSProperties = {
  background: 'var(--tg-theme-secondary-bg-color)', borderRadius: 20,
  padding: 16, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
};
const inputSt: React.CSSProperties = {
  width: '100%', borderRadius: 14, padding: '10px 14px',
  background: 'var(--tg-theme-bg-color)', color: 'var(--tg-theme-text-color)',
  border: '1px solid rgba(255,255,255,0.12)', fontSize: 14, outline: 'none',
  display: 'block',
};
const labelSt: React.CSSProperties = {
  fontSize: 12, color: 'var(--tg-theme-hint-color)', marginBottom: 4, display: 'block',
};
const rowSt: React.CSSProperties = { marginBottom: 16 };
const flexRowSt: React.CSSProperties = { display: 'flex', gap: 8 };
const tierActiveSt: React.CSSProperties = {
  flex: 1, padding: '8px 0', borderRadius: 12,
  background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
  border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const tierInactiveSt: React.CSSProperties = {
  ...tierActiveSt, background: 'rgba(255,255,255,0.07)', color: 'var(--tg-theme-hint-color)',
};
const skillActiveSt: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 20,
  background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
  border: 'none', fontSize: 12, cursor: 'pointer', marginRight: 6, marginBottom: 6,
};
const skillInactiveSt: React.CSSProperties = {
  ...skillActiveSt, background: 'rgba(255,255,255,0.08)', color: 'var(--tg-theme-text-color)',
};
const switchRowSt: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16,
};
const testBoxSt: React.CSSProperties = {
  borderRadius: 14, padding: 12, background: 'rgba(255,255,255,0.05)', marginBottom: 16,
};
const savePanelSt: React.CSSProperties = {
  position: 'fixed', bottom: 0, left: 0, right: 0,
  padding: 16, background: 'var(--tg-theme-bg-color)',
  borderTop: '1px solid rgba(255,255,255,0.06)',
};
const btnPrimSt: React.CSSProperties = {
  width: '100%', padding: '12px 0', borderRadius: 14,
  background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
  border: 'none', fontSize: 15, fontWeight: 600, cursor: 'pointer',
};
const addBtnSt: React.CSSProperties = {
  width: 36, height: 36, borderRadius: '50%',
  background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
  border: 'none', fontSize: 22, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const backBtnSt: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--tg-theme-hint-color)',
  fontSize: 20, cursor: 'pointer', padding: 0,
};
const centeredSt: React.CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 };
const emptyTextSt: React.CSSProperties = { textAlign: 'center', color: 'var(--tg-theme-hint-color)', fontSize: 14, marginTop: 48 };

export default function Agents() {
  const [agents,    setAgents]    = useState<Agent[]>([]);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [editId,    setEditId]    = useState<string | 'new' | null>(null);
  const [form,      setForm]      = useState<AgentCreate>(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [testIn,    setTestIn]    = useState('');
  const [testOut,   setTestOut]   = useState<string | null>(null);
  const [testing,   setTesting]   = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [a, s] = await Promise.all([agentsApi.list(), skillsApi.list()]);
      setAgents(a); setAllSkills(s);
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setForm(EMPTY); setEditId('new'); setTestIn(''); setTestOut(null); haptic('light'); };
  const openEdit = (a: Agent) => {
    setForm({ name: a.name, system_prompt: a.system_prompt, tier: a.tier, skills: a.skills, enabled: a.enabled });
    setEditId(a.id); setTestIn(''); setTestOut(null); haptic('light');
  };
  const closeForm = () => { setEditId(null); hideMainButton(); };

  const handleSave = useCallback(async () => {
    if (!form.name.trim() || !editId) return;
    setSaving(true);
    try {
      if (editId === 'new') {
        const created = await agentsApi.create(form);
        setAgents((prev) => [...prev, created]);
      } else {
        const updated = await agentsApi.update(editId, form as AgentUpdate);
        setAgents((prev) => prev.map((a) => a.id === editId ? updated : a));
      }
      closeForm(); haptic('medium');
    } catch (e) { setError(String(e)); }
    finally { setSaving(false); }
  }, [form, editId]);

  useEffect(() => {
    if (!editId) return;
    const label = editId === 'new' ? 'Создать' : 'Сохранить';
    showMainButton(label, handleSave);
    return () => hideMainButton();
  }, [editId, handleSave]);

  const handleToggle = async (a: Agent, enabled: boolean) => {
    haptic('light');
    setAgents((prev) => prev.map((x) => x.id === a.id ? { ...x, enabled } : x));
    try { await agentsApi.setEnabled(a.id, enabled); }
    catch { setAgents((prev) => prev.map((x) => x.id === a.id ? { ...x, enabled: !enabled } : x)); }
  };

  const handleTest = async () => {
    if (!editId || editId === 'new' || !testIn.trim()) return;
    setTesting(true); setTestOut(null);
    try { const r = await agentsApi.test(editId, testIn); setTestOut(r.output); }
    catch (e) { setTestOut('Ошибка: ' + String(e)); }
    finally { setTesting(false); }
  };

  const toggleSkill = (id: string) =>
    setForm((f) => ({
      ...f,
      skills: f.skills.includes(id) ? f.skills.filter((s) => s !== id) : [...f.skills, id],
    }));

  if (loading) return <div style={centeredSt}><Spinner size={32} /></div>;
  if (error && !editId) return <ErrorBanner message={error} onRetry={load} />;

  // FORM VIEW
  if (editId !== null) {
    const taStyle: React.CSSProperties = { ...inputSt, resize: 'none' };
    const testInputSt: React.CSSProperties = { ...inputSt, marginBottom: 8 };
    const outSt: React.CSSProperties = { fontSize: 13, marginTop: 8, whiteSpace: 'pre-wrap', color: 'var(--tg-theme-text-color)', opacity: 0.8 };
    const testBtnSt: React.CSSProperties = {
      width: '100%', padding: 10, borderRadius: 12,
      background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
      border: 'none', fontSize: 14, cursor: 'pointer',
      opacity: testing || !testIn.trim() ? 0.5 : 1,
    };
    return (
      <div style={pageSt}>
        <div style={headerSt}>
          <button style={backBtnSt} onClick={closeForm}>←</button>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {editId === 'new' ? 'Новый агент' : 'Редактировать'}
          </span>
          <span style={{ width: 28 }} />
        </div>

        <div style={scrollSt}>
          <div style={rowSt}>
            <label style={labelSt}>Имя</label>
            <input style={inputSt} value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Название агента" />
          </div>

          <div style={rowSt}>
            <label style={labelSt}>System prompt</label>
            <textarea rows={5} style={taStyle}
              value={form.system_prompt}
              onChange={(e) => setForm((f) => ({ ...f, system_prompt: e.target.value }))}
              placeholder="Инструкция для агента" />
          </div>

          <div style={rowSt}>
            <label style={labelSt}>Модель (tier)</label>
            <div style={flexRowSt}>
              {TIERS.map((t) => (
                <button key={t} style={form.tier === t ? tierActiveSt : tierInactiveSt}
                  onClick={() => setForm((f) => ({ ...f, tier: t }))}>
                  {TIER_LABEL[t]}
                </button>
              ))}
            </div>
          </div>

          {allSkills.length > 0 && (
            <div style={rowSt}>
              <label style={labelSt}>Навыки</label>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {allSkills.map((s) => (
                  <button key={s.id}
                    style={form.skills.includes(s.id) ? skillActiveSt : skillInactiveSt}
                    onClick={() => toggleSkill(s.id)}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={switchRowSt}>
            <span style={{ fontSize: 15 }}>Активен</span>
            <Toggle checked={form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
          </div>

          {editId !== 'new' && (
            <div style={testBoxSt}>
              <label style={labelSt}>Тестирование</label>
              <input style={testInputSt} value={testIn}
                onChange={(e) => setTestIn(e.target.value)}
                placeholder="Введите сообщение" />
              <button style={testBtnSt} onClick={handleTest} disabled={testing || !testIn.trim()}>
                {testing ? '…' : '▶ Тест'}
              </button>
              {testOut && <p style={outSt}>{testOut}</p>}
            </div>
          )}

          {error && <ErrorBanner message={error} />}
        </div>

        <div style={savePanelSt}>
          <button style={btnPrimSt} onClick={handleSave} disabled={saving}>
            {saving ? '…' : (editId === 'new' ? 'Создать' : 'Сохранить')}
          </button>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div style={pageSt}>
      <div style={headerSt}>
        <h1 style={h1St}>Агенты</h1>
        <button style={addBtnSt} onClick={openNew}>+</button>
      </div>
      <div style={scrollSt}>
        {agents.length === 0 && (
          <p style={emptyTextSt}>Нет агентов. Нажмите + чтобы создать.</p>
        )}
        {agents.map((a) => (
          <div key={a.id} style={cardSt}>
            <button onClick={() => openEdit(a)}
              style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 500, fontSize: 15, color: 'var(--tg-theme-text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--tg-theme-hint-color)' }}>{TIER_LABEL[a.tier]} &bull; {a.skills.length} нав.</p>
            </button>
            <Toggle checked={a.enabled} onChange={(v) => handleToggle(a, v)} />
          </div>
        ))}
      </div>
    </div>
  );
}
